// Shared validation schemas using zod.
import { z } from "zod";

const PHONE_RE = /^[+()\d\s\-.\u00A0]{7,24}$/;
const optionalPhone = z
  .string()
  .trim()
  .optional()
  .or(z.literal(""))
  .transform((v) => (v === "" || v === undefined ? undefined : v))
  .refine((v) => v === undefined || PHONE_RE.test(v), {
    message: "Phone number format looks invalid",
  });

export const SERVICE_TYPES = [
  "Same-Day Local Courier",
  "Medical & Pharmaceutical Transport",
  "Last-Mile Parcel Delivery",
  "B2B Freight & Cargo",
  "Dedicated Route Contracts",
  "Not sure",
];

export const SUBJECTS = [
  "General Inquiry",
  "Service Question",
  "Partnership",
  "Other",
];

export const PICKUP_TIMES = ["ASAP", "Morning", "Afternoon", "Specific time"];

const requiredString = (label, min = 2, max = 120) =>
  z
    .string({ required_error: `${label} is required` })
    .trim()
    .min(min, `${label} is required`)
    .max(max, `${label} is too long`);

const optionalString = (max = 500) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .or(z.literal(""))
    .transform((v) => (v === "" || v === undefined ? undefined : v));

export const quickSchema = z.object({
  name: requiredString("Your name"),
  email: z.string().trim().email("Enter a valid email address"),
  phone: optionalPhone,
  pickup_city: requiredString("Pickup city"),
  dropoff_city: requiredString("Drop-off city"),
  service_type: z.enum(SERVICE_TYPES, { required_error: "Please choose a service" }),
  _hp_check: z.string().optional(),
});

export const quoteSchema = z.object({
  name: requiredString("Name"),
  company: optionalString(160),
  email: z.string().trim().email("Enter a valid email address"),
  phone: optionalPhone,
  service_type: z.enum(SERVICE_TYPES, { required_error: "Please choose a service" }),
  pickup_city: requiredString("Pickup city"),
  dropoff_city: requiredString("Drop-off city"),
  pickup_date: optionalString(40),
  pickup_time: z
    .enum(PICKUP_TIMES)
    .optional()
    .or(z.literal(""))
    .transform((v) => (v === "" || v === undefined ? undefined : v)),
  cargo_description: optionalString(500),
  weight_kg: z
    .union([z.string(), z.number()])
    .optional()
    .transform((v) => {
      if (v === undefined || v === "" || v === null) return undefined;
      const n = typeof v === "number" ? v : Number(v);
      return Number.isFinite(n) ? n : undefined;
    })
    .refine((v) => v === undefined || (v >= 0 && v <= 50000), {
      message: "Weight must be between 0 and 50000 kg",
    }),
  special_requirements: optionalString(500),
  _hp_check: z.string().optional(),
});

export const contactSchema = z.object({
  name: requiredString("Name"),
  email: z.string().trim().email("Enter a valid email address"),
  phone: optionalPhone,
  company: optionalString(160),
  subject: z.enum(SUBJECTS).default("General Inquiry"),
  message: requiredString("Message", 5, 2000),
  _hp_check: z.string().optional(),
});
